// @givmo/fundraisers-module — a fundraiser's Emails tab (EMC-WEB1; Email Campaign plan §1.1 step 4, §2.7),
// built to mockup 13 of the walkthrough Jonathan approved: the series on its dates, each with its audience
// and where it stands, under the line that says which address it goes out from, and "New email".
//
// ⚠ EVERY FACT IS THE SERVER'S. Whether emails serve here is the series read's answer (no web knob: the
// backend's `EMAIL_CAMPAIGNS_ENABLED` serves it, and dark it is the route miss); whether one can be
// scheduled is the sender read's `can_schedule` (`sender-identity.ts`, EMC-WEB3's); a row's audience is
// named by the names the organization gave it, from the audience options read.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, Copy, Mail, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  Menu,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { OrgConsoleUnavailableNotice } from '@givmo/org-console';
import { EMAILS_COPY as C } from './emails-copy';
import {
  audienceSummary,
  decodeEmail,
  emailDay,
  emailRefusal,
  emailStatus,
  emailTime,
  isEmailsUnavailable,
  seriesNeedsYear,
  type AudienceOptions,
  type EmailRow,
  type EmailStatusKind,
} from './emails-decode';
import {
  audienceOptionsOptions,
  emailKeys,
  emailResultsSummaryOptions,
  emailsSeriesOptions,
} from './emails-queries';
import { EmailResultsSection, emailsWithResults } from './email-results';
import { createEmail } from './emails-transport';
import { emailDeletable, useDeleteEmail } from './email-delete';
import { useDuplicateEmail } from './email-duplicate';
import { mayWriteFundraisers } from './fundraisers-config';
import { useFundraisersModule } from './fundraisers-context';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import { FundraisersLoading } from './fundraisers-state';
import { guessDomain, SendingDomainCard } from './sending-domain-card';
import { SENDING_DOMAIN_COPY } from './sending-domain-copy';
import { useSenderIdentity } from './sender-identity';
import { useLivePage } from './use-live-page';

/** The status's colour: the series' plain words, a warning for a late email. A draft and a cancelled
 *  email wear a neutral chip, so an email not yet set to go out never reads like one that is (the
 *  polish-1 user test: "Scheduled" and "Draft" looked exactly alike). */
const STATUS_CLASS: Record<EmailStatusKind, string> = {
  draft: '',
  scheduled: 'text-text-strong',
  held: 'font-w600 text-warning-700',
  late: 'font-w600 text-warning-700',
  going: 'text-text-strong',
  delivered: 'text-text-strong',
  noOne: 'text-text-secondary',
  cancelled: '',
  failed: 'font-w600 text-danger-700',
};
const CHIP_STATUS: ReadonlySet<EmailStatusKind> = new Set(['draft', 'cancelled']);

/** "Fri, Nov 20 · 9:00 AM", the day strong and the time quiet, with its year when the series needs one;
 *  "Not scheduled" without a time. */
function SendsCell({ row, year }: { row: EmailRow; year: boolean }) {
  if (row.scheduledFor === null) return <span className="text-text-secondary">{C.noTime}</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="text-text-strong">{emailDay(row.scheduledFor, row.tz, { year })}</span>
      <span className="text-text-secondary"> · {emailTime(row.scheduledFor, row.tz)}</span>
    </span>
  );
}

function statusWhy(row: EmailRow, kind: EmailStatusKind): string | null {
  if (kind === 'held' && row.heldReason !== null) return C.heldShort[row.heldReason];
  if (kind !== 'failed') return null;
  const reason = row.heldReason ?? row.failureReason;
  return reason === null ? null : C.failedShort[reason];
}

function StatusCell({ row, now }: { row: EmailRow; now: Date }) {
  const kind = emailStatus(row, now);
  const testId = `fundraiser-email-status-${row.id}`;
  if (CHIP_STATUS.has(kind))
    return (
      <Badge tone="neutral" size="md" testId={testId}>
        {C.status[kind]}
      </Badge>
    );
  // A held or failed email says why in a short line under its status (the KCC dev build); its page says
  // what to do.
  const why = statusWhy(row, kind);
  return (
    <span className="flex flex-col">
      <span className={STATUS_CLASS[kind]} {...testIdProp(testId)}>
        {C.status[kind]}
      </span>
      {why !== null && (
        <span className="text-s13 text-text-secondary" {...testIdProp(`${testId}-why`)}>
          {why}
        </span>
      )}
    </span>
  );
}

function audienceWords(row: EmailRow, options: AudienceOptions | undefined): string {
  return (
    audienceSummary(row.audience, options, {
      everyone: C.everyone,
      campaignDonors: C.campaignDonors,
      more: C.more,
    }) ?? '—'
  );
}

/** The tab's own address while no series serves here (the Donors tab's pattern). */
/** A row's own acts, beside its chevron: Duplicate, and Delete for an email not scheduled. Its press never
 *  opens the row. */
function RowMenu({
  row,
  busy,
  onDuplicate,
  onDelete,
  place = 'email',
}: {
  row: EmailRow;
  busy: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  /** The table's row (`email`), or the narrow screen's card (`email-card`). */
  place?: 'email' | 'email-card';
}) {
  const name = row.subject || C.noSubject;
  return (
    <span
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      className="inline-flex"
    >
      <Menu.Root modal={false}>
        <Menu.Trigger
          disabled={busy}
          aria-label={C.rowActions(name)}
          render={
            <IconButton
              aria-label={C.rowActions(name)}
              size="sm"
              testId={`fundraiser-${place}-menu-${row.id}`}
            />
          }
        >
          <Icon icon={MoreHorizontal} size={16} aria-hidden />
        </Menu.Trigger>
        <Menu.Content saas align="end" sideOffset={6}>
          <Menu.Item
            saas
            startIcon={<Icon icon={Copy} size={15} aria-hidden />}
            onClick={onDuplicate}
            testId={`fundraiser-${place}-duplicate-${row.id}`}
          >
            {C.duplicate}
          </Menu.Item>
          {emailDeletable(row) && (
            <Menu.Item
              saas
              tone="danger"
              startIcon={<Icon icon={Trash2} size={15} aria-hidden />}
              onClick={onDelete}
              testId={`fundraiser-${place}-delete-${row.id}`}
            >
              {C.delete}
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu.Root>
    </span>
  );
}

export function EmailsUnavailable() {
  return (
    <OrgConsoleUnavailableNotice
      testId="fundraiser-emails-unavailable"
      title={C.unavailableTitle}
      description={C.unavailableBody}
    />
  );
}

export function EmailsTab({ fundraiserId }: { fundraiserId: string }) {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const client = useQueryClient();
  const live = useLivePage();
  const series = useQuery({
    ...emailsSeriesOptions(orgRef, fundraiserId),
    enabled: config.orgRef !== null,
  });
  const options = useQuery({
    ...audienceOptionsOptions(orgRef, fundraiserId),
    enabled: series.isSuccess,
  });
  const sender = useSenderIdentity(config.orgRef);
  const writable = mayWriteFundraisers(config);
  const open = (row: EmailRow) => config.onNavigate(FUNDRAISERS_ROUTES.email(fundraiserId, row.id));
  const create = useMutation({
    mutationFn: () => createEmail(orgRef, fundraiserId),
    onSuccess: async (body) => {
      const row = decodeEmail(body);
      await client.invalidateQueries({ queryKey: emailKeys.series(orgRef, fundraiserId) });
      if (!live()) return;
      if (row === null) toast.error(C.failed);
      else config.onNavigate(FUNDRAISERS_ROUTES.emailDesign(fundraiserId, row.id));
    },
    onError: (error) => {
      if (!live()) return;
      const refusal = emailRefusal(error);
      toast.error(refusal.kind === 'refused' ? refusal.message : C.failed);
    },
  });

  const duplicate = useDuplicateEmail(fundraiserId);
  const remove = useDeleteEmail(fundraiserId);
  const menuBusy = duplicate.isPending || remove.isPending;

  const now = new Date();
  const rows = series.data ?? [];
  const year = seriesNeedsYear(rows, now);
  // EMC-WEB2 (mockup 20): once emails went out, what each raised leads the tab, and the series below
  // holds the emails still to go out.
  const withResults = emailsWithResults(rows);
  const summary = useQuery({
    ...emailResultsSummaryOptions(orgRef, fundraiserId),
    enabled: series.isSuccess && withResults.length > 0,
  });
  const upcoming = withResults.length > 0 ? rows.filter((row) => row.results === null) : rows;
  const next =
    rows.find(
      (row) =>
        row.state === 'scheduled' &&
        row.scheduledFor !== null &&
        new Date(row.scheduledFor).getTime() > now.getTime(),
    ) ?? null;
  const identity = sender.data ?? null;
  const columns: DataTableColumn<EmailRow>[] = [
    { id: 'sends', header: C.columns.sends, cell: (row) => <SendsCell row={row} year={year} /> },
    {
      id: 'email',
      header: C.columns.email,
      // The subject is the row's link (a keyboard reaches it; the row's click is the mouse's).
      cell: (row) => (
        <button
          type="button"
          className="text-left font-w600 text-text-strong hover:text-brand-700 hover:underline focus-visible:outline-2 focus-visible:outline-brand-500"
          onClick={(event) => {
            event.stopPropagation();
            open(row);
          }}
          {...testIdProp(`fundraiser-email-open-${row.id}`)}
        >
          {row.untouchedCopy && <span className="font-w400 text-text-tertiary">{C.copyOf} </span>}
          {row.subject || C.noSubject}
        </button>
      ),
    },
    {
      id: 'audience',
      header: C.columns.audience,
      cell: (row) => (
        <span className="text-text-secondary">{audienceWords(row, options.data)}</span>
      ),
    },
    { id: 'status', header: C.columns.status, cell: (row) => <StatusCell row={row} now={now} /> },
    {
      id: 'open',
      header: <span className="sr-only">{C.columns.open}</span>,
      cell: (row) => (
        <span className="flex items-center justify-end gap-4">
          {writable && (
            <RowMenu
              row={row}
              busy={menuBusy}
              onDuplicate={() => duplicate.start(row.id)}
              onDelete={() => remove.start(row)}
            />
          )}
          <ChevronRight aria-hidden className="size-16 text-text-tertiary" />
        </span>
      ),
    },
  ];

  const ready = identity?.state === 'ready' && identity.canSchedule;
  const newEmail = writable && (
    <Button
      appearance="saas"
      // While the domain isn't ready, setting it up is the page's main act; writing still opens here.
      tone={ready ? 'primary' : 'secondary'}
      testId="fundraiser-emails-new"
      loading={create.isPending}
      disabled={create.isPending}
      leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
      onClick={() => create.mutate()}
    >
      {C.newEmail}
    </Button>
  );

  return (
    <div className="flex min-w-0 flex-col gap-16" {...testIdProp('fundraiser-emails')}>
      {duplicate.dialog}
      {remove.dialog}
      {/* EMC-WEB3's card says where the sending domain stands (mockups 10–12). Once the domain is
          ready and the series has emails, one line says it (mockup 13). */}
      {(!ready || !series.isSuccess || rows.length === 0) && <SendingDomainCard />}
      {series.isError ? (
        isEmailsUnavailable(series.error) ? (
          <EmailsUnavailable />
        ) : (
          <EmptyState
            testId="fundraiser-emails-error"
            title={C.loadFailed}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                testId="fundraiser-emails-retry"
                onClick={() => void series.refetch()}
              >
                {C.retry}
              </Button>
            }
          />
        )
      ) : series.isPending ? (
        <FundraisersLoading />
      ) : (
        <>
          <EmailResultsSection
            rows={rows}
            rowActions={
              writable
                ? (row, place) => (
                    <RowMenu
                      row={row}
                      place={place}
                      busy={menuBusy}
                      onDuplicate={() => duplicate.start(row.id)}
                      onDelete={() => remove.start(row)}
                    />
                  )
                : undefined
            }
            summary={summary.data}
            next={next}
            now={now}
            describe={(row) =>
              row.audience === null || options.data === undefined
                ? null
                : audienceSummary(row.audience, options.data, {
                    everyone: C.everyone,
                    campaignDonors: C.campaignDonors,
                    more: C.more,
                  })
            }
            onOpen={open}
          />
          {withResults.length > 0 && upcoming.length > 0 && (
            <h2
              className="mt-8 font-sans text-s16 font-w600 text-text-strong"
              {...testIdProp('fundraiser-emails-coming-up')}
            >
              {C.comingUp}
            </h2>
          )}
          {rows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-12">
              {ready && identity.fromAddress !== null ? (
                <p
                  className="inline-flex min-w-0 items-center gap-8 text-s15 text-text-secondary"
                  {...testIdProp('fundraiser-emails-sender')}
                >
                  <Check aria-hidden className="size-16 shrink-0 text-success-600" />
                  <span>
                    {C.sendingAs}{' '}
                    <span className="font-w600 text-text-strong wrap-anywhere">
                      {identity.fromAddress}
                    </span>
                  </span>
                </p>
              ) : (
                <span />
              )}
              {newEmail}
            </div>
          )}
          {rows.length === 0 ? (
            // Mockup 12's empty series, in EMC-WEB3's words, with the act inside it.
            <Card testId="fundraiser-emails-empty-card">
              <CardBody>
                <EmptyState
                  testId="fundraiser-emails-empty"
                  icon={<Icon icon={Mail} size={20} aria-hidden />}
                  title={SENDING_DOMAIN_COPY.emptyTitle}
                  description={
                    <span className="block text-balance">
                      {ready
                        ? SENDING_DOMAIN_COPY.emptyAfterDomain
                        : SENDING_DOMAIN_COPY.emptyBeforeDomain(
                            identity?.domain ??
                              identity?.suggestedDomain ??
                              guessDomain(config.viewerEmail),
                          )}
                    </span>
                  }
                  action={newEmail || undefined}
                />
              </CardBody>
            </Card>
          ) : upcoming.length === 0 ? null : (
            <>
              <div className="hidden lg:block [&_tbody_tr]:cursor-pointer">
                <DataTable
                  testId="fundraiser-emails-table"
                  columns={columns}
                  rows={upcoming}
                  rowKey={(row) => row.id}
                  rowTestId={(row) => `fundraiser-email-row-${row.id}`}
                  onRowClick={open}
                />
              </div>
              <div
                className="flex flex-col gap-12 lg:hidden"
                {...testIdProp('fundraiser-email-cards')}
              >
                {upcoming.map((row) => (
                  <Card
                    key={row.id}
                    flush
                    className="relative"
                    testId={`fundraiser-email-card-${row.id}`}
                  >
                    {writable && (
                      <span className="absolute bottom-10 right-10 z-10">
                        <RowMenu
                          row={row}
                          place="email-card"
                          busy={menuBusy}
                          onDuplicate={() => duplicate.start(row.id)}
                          onDelete={() => remove.start(row)}
                        />
                      </span>
                    )}
                    <button
                      type="button"
                      className="w-full rounded-lg text-left focus-visible:outline-2 focus-visible:outline-brand-500"
                      onClick={() => open(row)}
                    >
                      <CardBody className="flex flex-col gap-6 p-16">
                        <div className="flex items-start justify-between gap-12">
                          <p className="min-w-0 text-s16 font-w600 text-text-strong wrap-anywhere">
                            {row.untouchedCopy && (
                              <span className="font-w400 text-text-tertiary">{C.copyOf} </span>
                            )}
                            {row.subject || C.noSubject}
                          </p>
                          <p className="shrink-0 text-s14">
                            <StatusCell row={row} now={now} />
                          </p>
                        </div>
                        <p className="flex items-center justify-between gap-12 text-s14">
                          <SendsCell row={row} year={year} />
                          <ChevronRight
                            aria-hidden
                            className="size-16 shrink-0 text-text-tertiary"
                          />
                        </p>
                        <p className="text-s14 text-text-secondary">
                          {audienceWords(row, options.data)}
                        </p>
                      </CardBody>
                    </button>
                  </Card>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
